// COP — MIL-STD-2525 / NATO APP-6 style symbology sprites (self-contained).
//
// Powers the three-way symbology carrier ("dot" ⇄ "milsym" ⇄ "coalition"):
//   - the STANDARD ("milsym") frames are dependency-free — this module approximates
//     the APP-6 symbol frames with the Canvas 2D API and emits each symbol as its
//     own transparent PNG data URL, sized for deck.gl's IconLayer and cached by key
//     so each (affiliation, domain) sprite is rasterised exactly once;
//   - the COALITION mode renders FULL MIL-STD-2525 symbols via `milsymbol`, loaded
//     LAZILY (a dynamic import → its own vite chunk) only when coalition is first
//     selected. Until the chunk arrives — and for any CoT type a 2525 SIDC cannot
//     be derived from — the renderer falls back to the milsym sprite (never blank,
//     never a throw). See {@link cotTypeToSidc} / {@link coalitionIcon}.
//   - the ALERT GLYPH family ({@link alertIcon}) — kind:"alert" envelopes wear an
//     EVENT glyph, not a unit's domain glyph. In "framed" (milsym/coalition) mode the
//     event rides the SAME affiliation FRAME the unit markers use ({@link affiliationFrameShape}
//     → hostile=diamond, friend=rectangle, neutral=square, unknown/pending=quatrefoil),
//     so a hostile alert reads as a red DIAMOND (not a neutral square) — consistent with
//     a hostile unit — while the interior warning-triangle+"!" is the "event, not a unit"
//     channel. In "minimal" (dot) mode the low-ink filled triangle carries the event; in
//     both modes affiliation is ALSO the COLOR channel (neutral NWS/USGS alerts green,
//     hostile red).
//   - the DOT-MODE HOSTILE DIAMOND ({@link dotDiamondIcon}) — in the MINIMAL ("dot")
//     carrier, hostile-affiliation markers wear a rotated-square (diamond) MASK dot
//     tinted by deck's getColor, so hostility keeps a SHAPE channel (the 2525 hostile
//     frame) without requiring Standard mode.
//
// ── APP-6 / 2525 mapping (approximate) ────────────────────────────────────
//
//   FRAME SHAPE encodes AFFILIATION × DIMENSION (domain), edging toward the
//   canonical MIL-STD-2525C / NATO APP-6 frame set (cf. milsymbol). The base
//   shape is the affiliation; the dimension restyles it (ground vs sea-surface
//   vs air [open base] vs subsurface [open top]):
//
//                 ground       sea-surface   air            subsurface
//     friend      rectangle    circle        arch (round)   inverted arch
//     hostile     diamond      diamond       peak/pentagon  inverted peak
//     neutral     square       square        square (open)  square (open)
//     unknown     quatrefoil   quatrefoil    3-lobe cloud   inverted cloud
//     pending     = the unknown shape, drawn with a DASHED stroke ("suspect")
//
//   The unknown/pending frame is a true rounded CLOVERLEAF (four cardinal lobes
//   meeting at re-entrant diagonal notches) — NOT a spiky star/fan.
//
//   DIMENSION value → frame-domain mapping (frameDomainOf):
//     ground → ground · maritime → sea · air/uas/space → air · subsurface → subsurface
//   (No current COP feed emits "subsurface"; that frame is latent until such
//   data exists. space reuses the open-base air frame plus the space dimension
//   cap drawn above the frame.)
//
//   A central GLYPH and an above-frame DIMENSION modifier still hint the domain:
//     air → up-wedge · uas → fixed-wing · maritime/subsurface → ship-hull ·
//     ground → cross/dot · space → 4-point star (+ top arc cap above the frame)
//
// Colours: the affiliation hue draws the frame stroke (crisp 2px) and a
// moderate-alpha fill; the glyph is drawn in the same hue at full strength.
// Background is transparent. Sprites are ~40×40 CSS px, rasterised at
// devicePixelRatio so they stay sharp on retina displays.
//
// ── Wiring into a deck.gl IconLayer ───────────────────────────────────────
//
// No iconAtlas / iconMapping is needed because every symbol is its own data
// URL. Use the per-object `getIcon` form, which deck.gl accepts when each
// returned object carries {url, width, height, anchorX, anchorY, id}:
//
//   import { IconLayer } from "@deck.gl/layers";
//   import { symbolIcon, type SymbolColors } from "./symbology.ts";
//
//   const colors: SymbolColors = affiliationColorsFor(theme); // hex per affiliation
//
//   new IconLayer({
//     id,
//     data,                               // CanonicalEnvelope[]
//     pickable: true,
//     getPosition: (e) => pointOf(e) ?? [0, 0],
//     // The `id` MUST be stable per distinct sprite so deck.gl dedupes the
//     // texture upload; `${affiliation}|${domain}` is exactly that key.
//     getIcon: (e) => {
//       const aff = e.properties.affiliation ?? "unknown";
//       const dom = String(e.properties.domain ?? "");
//       const ic = symbolIcon(aff, dom, colors);
//       return { url: ic.url, width: ic.width, height: ic.height,
//                anchorX: ic.anchorX, anchorY: ic.anchorY,
//                id: `${aff}|${dom}` };
//     },
//     sizeUnits: "pixels",
//     getSize: 40,                        // device-px sprite drawn at 40 CSS px
//     // Re-evaluate getIcon when the colour theme (mode/palette) changes:
//     updateTriggers: { getIcon: [colorsKey, frameN] },
//   });
//
// In "beautiful" mode the orchestrator keeps the existing Scatterplot marker
// stack (deckBackend.ts); in "milsym" mode it swaps in the IconLayer above.

/** Affiliation → hex colour (#RRGGBB). Shape-compatible with the host's
 *  AffiliationColors so the same resolved palette feeds both render modes. */
export interface SymbolColors {
  friend: string;
  hostile: string;
  neutral: string;
  unknown: string;
  pending: string;
}

/** A deck.gl-IconLayer-ready icon descriptor. `url` is a self-contained PNG
 *  data URL; `width`/`height` are the rasterised pixel dimensions; the anchor
 *  centres the sprite on the track position. */
export interface SymbolIcon {
  url: string;
  width: number;
  height: number;
  anchorX: number;
  anchorY: number;
}

// ── Tunables ───────────────────────────────────────────────────────────────

/** Logical (CSS) sprite size in px. Rasterised ×DPR for retina sharpness. */
const SPRITE_CSS = 40;
/** Frame stroke width in CSS px (crisp tactical line). Raised from 2 → 2.5 so the
 *  affiliation frame reads over mid-grey basemap tiles, not just dark surfaces. */
const STROKE_CSS = 2.5;
/** Affiliation fill alpha. Raised from 0.22 → 0.32 for frame legibility over
 *  mid-tone tiles (still translucent enough that the basemap shows through). */
const FILL_ALPHA = 0.32;

type Affil = keyof SymbolColors;
const AFFILIATIONS: readonly Affil[] = [
  "friend",
  "hostile",
  "neutral",
  "unknown",
  "pending",
];

type Domain = "air" | "maritime" | "ground" | "uas" | "space" | "subsurface";
const DOMAINS: readonly Domain[] = [
  "air",
  "maritime",
  "ground",
  "uas",
  "space",
  "subsurface",
];

/** Normalise free-form affiliation input to a known frame; default unknown. */
export function normAffil(a: string): Affil {
  const k = (a ?? "").toLowerCase().trim();
  return (AFFILIATIONS as readonly string[]).includes(k) ? (k as Affil) : "unknown";
}

/** Normalise free-form domain input to a known dimension; default ground. */
export function normDomain(d: string): Domain {
  const k = (d ?? "").toLowerCase().trim();
  return (DOMAINS as readonly string[]).includes(k) ? (k as Domain) : "ground";
}

// ── Colour helpers ───────────────────────────────────────────────────────────

/** Parse #RRGGBB (or #RGB) into [r,g,b]; falls back to neutral grey. */
function hexToRgb(hex: string): [number, number, number] {
  let h = (hex ?? "").trim().replace(/^#/, "");
  if (h.length === 3) h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2];
  if (!/^[0-9a-f]{6}$/i.test(h)) return [156, 163, 175];
  const n = parseInt(h, 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

function rgba(rgb: [number, number, number], alpha: number): string {
  return `rgba(${rgb[0]},${rgb[1]},${rgb[2]},${alpha})`;
}

// ── Sprite cache ─────────────────────────────────────────────────────────────

const cache = new Map<string, SymbolIcon>();

/** 1×1 transparent PNG — the SSR / no-document fallback so callers never get a
 *  broken icon descriptor when there is no canvas to draw on. */
const TRANSPARENT_PX: SymbolIcon = {
  url:
    "data:image/png;base64," +
    "iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNk+M9QDwADhgGAWjR9awAAAABJRU5ErkJggg==",
  width: 1,
  height: 1,
  anchorX: 0,
  anchorY: 0,
};

/**
 * Return a cached deck.gl-IconLayer-ready APP-6/2525 symbol for the given
 * affiliation + domain. The sprite is rasterised once per `${affiliation}|${domain}`
 * key and reused thereafter. Falls back to a 1×1 transparent pixel under SSR or
 * any environment lacking `document` / a 2D canvas context.
 */
export function symbolIcon(
  affiliation: string,
  domain: string,
  colors: SymbolColors,
): SymbolIcon {
  const aff = normAffil(affiliation);
  const dom = normDomain(domain);
  // Cache by MODE|affiliation|domain|colour so a theme/palette swap regenerates the
  // sprite rather than serving a stale-hued one, while same-theme repeats hit. The
  // leading mode discriminator keeps the shared cache collision-free across the
  // milsym / coalition / alert sprite families (their keys carry their own prefixes).
  const key = `milsym|${aff}|${dom}|${colors[aff]}`;
  const hit = cache.get(key);
  if (hit) return hit;

  const icon = draw(aff, dom, colors);
  cache.set(key, icon);
  return icon;
}

/** Clear the sprite cache (e.g. after a full theme swap). */
export function clearSymbolCache(): void {
  cache.clear();
}

// ── COALITION mode — full MIL-STD-2525 symbols via milsymbol (lazy) ─────────────

/** The minimal structural slice of milsymbol this module consumes. Typed narrow +
 *  local so the dependency stays behind the dynamic-import seam (the module is a
 *  vite code-split chunk that loads only when coalition mode is first selected). */
interface MilsymbolSymbol {
  toDataURL(): string;
  getSize(): { width: number; height: number };
  getAnchor(): { x: number; y: number };
}
interface MilsymbolModule {
  Symbol: new (sidc: string, options?: Record<string, unknown>) => MilsymbolSymbol;
}

let milsymbolMod: MilsymbolModule | null = null;
let milsymbolPromise: Promise<boolean> | null = null;
let milsymbolWarned = false;

/** Is the milsymbol chunk loaded and ready to rasterise coalition symbols? */
export function milsymbolReady(): boolean {
  return milsymbolMod !== null;
}

/** Kick (or join) the lazy milsymbol load. Single-flight; resolves true when the
 *  chunk is usable, false on a load failure (logged once — the renderer keeps
 *  falling back to the milsym sprites, so a CDN/offline failure degrades, never
 *  breaks). A failed load clears the in-flight promise so a later rebuild retries. */
// RETRY BACKOFF: coalition mode re-kicks loadMilsymbol on EVERY rebuild while the chunk is not
// ready, so a genuine load failure (offline / no cached chunk) at the ~1 Hz feed cadence would
// fire a fresh dynamic import() every second forever. We DO want a retry when connectivity is
// restored — so after a failure, short-circuit to `false` (no new import attempt) until this
// cooldown elapses. One real network attempt per 30s, not per frame.
const MILSYMBOL_RETRY_COOLDOWN_MS = 30_000;
let milsymbolRetryAfter = 0; // epoch ms; 0 = no prior failure

export function loadMilsymbol(): Promise<boolean> {
  if (milsymbolMod) return Promise.resolve(true);
  // In a cooldown window after a failure, don't launch another import — the caller falls back to
  // the built-in 2525 frames for this rebuild (guarded for a headless/no-Date context).
  if (!milsymbolPromise && milsymbolRetryAfter > 0) {
    let now = 0;
    try {
      now = Date.now();
    } catch {
      now = milsymbolRetryAfter; // no clock ⇒ treat as elapsed (allow the retry)
    }
    if (now < milsymbolRetryAfter) return Promise.resolve(false);
  }
  if (!milsymbolPromise) {
    milsymbolPromise = import("milsymbol")
      .then((m) => {
        const mod = (m as { default?: unknown }).default ?? m;
        if (!mod || typeof (mod as MilsymbolModule).Symbol !== "function") {
          throw new Error("milsymbol module has no Symbol constructor");
        }
        milsymbolMod = mod as MilsymbolModule;
        milsymbolRetryAfter = 0;
        return true;
      })
      .catch((err) => {
        if (!milsymbolWarned) {
          milsymbolWarned = true;
          console.warn("[symbology] milsymbol load failed — coalition mode falls back to built-in 2525 frames", err);
        }
        milsymbolPromise = null; // allow a later retry (e.g. connectivity restored)…
        try {
          milsymbolRetryAfter = Date.now() + MILSYMBOL_RETRY_COOLDOWN_MS; // …but not before the cooldown
        } catch {
          milsymbolRetryAfter = 0;
        }
        return false;
      });
  }
  return milsymbolPromise;
}

/** TEST-ONLY: inject/clear a milsymbol stand-in so the coalition path is testable
 *  headlessly without the real chunk. Not for app code. */
export function __setMilsymbolForTest(mod: unknown): void {
  milsymbolMod = (mod as MilsymbolModule) ?? null;
  milsymbolPromise = null;
}

/** CoT-type affiliation char (field 2) → 2525 SIDC affiliation (SIDC pos 2). The
 *  canonical five map per the task vocabulary; a/s/j/k are the remaining valid 2525
 *  affiliations CoT can carry (assumed-friend / suspect / joker / faker). Anything
 *  else ⇒ no SIDC (fallback sprite). */
const COT_AFFIL_TO_SIDC: Readonly<Record<string, string>> = Object.freeze({
  f: "F",
  h: "H",
  n: "N",
  u: "U",
  p: "P",
  a: "A",
  s: "S",
  j: "J",
  k: "K",
});

/** Valid 2525C battle-dimension codes (SIDC pos 3) — CoT field 3 uses the same letters. */
const SIDC_BATTLE_DIMS: ReadonlySet<string> = new Set(["P", "A", "G", "S", "U", "F", "Z", "X"]);

/**
 * Derive a MIL-STD-2525C 15-char SIDC from a TAK CoT type string ("a-<affil>-
 * <battle-dim>-<function...>", e.g. "a-f-G-U-C" → "SFGPUC---------").
 *
 * PURE + TOTAL: never throws; returns null for anything a symbol id cannot be
 * derived from (non-atom types like "b-m-p-…"/"t-x-…", unknown affiliation or
 * battle dimension, missing fields) — the caller then falls back to the built-in
 * milsym sprite, so a malformed feed value can never blank an icon.
 *
 * Mapping: SIDC = "S" (warfighting) + affiliation (f/h/n/u/p→F/H/N/U/P, plus
 * a/s/j/k) + battle dimension (CoT field 3, case-insensitive) + "P" (present) +
 * up to 6 function-id chars from the remaining single-alphanumeric CoT fields
 * (stops at the first non-conforming field), '-' padded to 15 chars.
 */
export function cotTypeToSidc(cotType: string): string | null {
  if (typeof cotType !== "string") return null;
  const fields = cotType.trim().split("-");
  if (fields.length < 3) return null;
  if (fields[0].toLowerCase() !== "a") return null; // atoms only — nothing else is a unit symbol
  const affil = COT_AFFIL_TO_SIDC[fields[1].toLowerCase()];
  if (!affil) return null;
  const dim = fields[2].toUpperCase();
  if (dim.length !== 1 || !SIDC_BATTLE_DIMS.has(dim)) return null;
  let fn = "";
  for (let i = 3; i < fields.length && fn.length < 6; i++) {
    const tok = fields[i].toUpperCase();
    if (!/^[A-Z0-9]$/.test(tok)) break; // stop at the first non-single-alphanumeric field
    fn += tok;
  }
  return `S${affil}${dim}P${fn.padEnd(6, "-")}-----`;
}

/** Coalition sprite size (CSS px) fed to milsymbol; rasterised ×DPR for retina. */
const COALITION_SIZE_CSS = 32;

/**
 * A cached deck.gl-ready FULL MIL-STD-2525 symbol for `sidc`, rendered by
 * milsymbol in the active affiliation palette. Returns null when the milsymbol
 * chunk is not loaded yet, under SSR, or on ANY milsymbol failure — the caller
 * falls back to {@link symbolIcon} (never blank, never a throw). Cached per
 * (sidc | palette) so each distinct symbol rasterises exactly once per theme.
 */
export function coalitionIcon(sidc: string, colors: SymbolColors): SymbolIcon | null {
  if (!milsymbolMod || typeof document === "undefined") return null;
  if (typeof sidc !== "string" || sidc.length !== 15) return null;
  const key = `coalition|${sidc}|${colors.friend}|${colors.hostile}|${colors.neutral}|${colors.unknown}|${colors.pending}`;
  const hit = cache.get(key);
  if (hit) return hit;
  try {
    const dpr =
      typeof window !== "undefined" && window.devicePixelRatio
        ? Math.max(1, Math.min(window.devicePixelRatio, 3))
        : 1;
    const sym = new milsymbolMod.Symbol(sidc, {
      size: Math.round(COALITION_SIZE_CSS * dpr),
      // Single-sourced palette: milsymbol picks the fill by the SIDC affiliation from
      // this custom colorMode, so coalition frames recolour with the active theme just
      // like the built-in sprites. (Civilian has no COP hue — neutral is the closest.)
      colorMode: {
        Civilian: colors.neutral,
        Friend: colors.friend,
        Hostile: colors.hostile,
        Neutral: colors.neutral,
        Unknown: colors.unknown,
        Suspect: colors.pending, // suspect = not-yet-confirmed — the pending hue
      },
    });
    const size = sym.getSize();
    const url = sym.toDataURL();
    if (
      !url ||
      typeof url !== "string" ||
      !Number.isFinite(size?.width) ||
      !Number.isFinite(size?.height) ||
      size.width <= 0 ||
      size.height <= 0
    ) {
      return null;
    }
    const anchor = sym.getAnchor();
    const icon: SymbolIcon = {
      url,
      width: Math.round(size.width),
      height: Math.round(size.height),
      // milsymbol's anchor is the symbol's octagon centre — anchoring there centres
      // the frame on the track position (mirrors the built-in sprites' centre anchor).
      anchorX: Number.isFinite(anchor?.x) ? anchor.x : size.width / 2,
      anchorY: Number.isFinite(anchor?.y) ? anchor.y : size.height / 2,
    };
    cache.set(key, icon);
    return icon;
  } catch {
    return null; // any milsymbol failure ⇒ fallback sprite (fail to the built-in frame)
  }
}

// ── ALERT GLYPH family — kind:"alert" envelopes wear an EVENT glyph, not a unit frame ──

/** Which alert glyph treatment to draw: "minimal" (dot mode — a filled warning
 *  triangle with an "!" cutout) or "framed" (milsym/coalition modes — the affiliation
 *  FRAME with a warning-triangle+"!" event glyph inside). */
export type AlertGlyphStyle = "minimal" | "framed";

/** The 2525 base (ground) frame SHAPE for an affiliation. This is the SAME shape the
 *  unit markers draw (framePath_friend/hostile/neutral/unknown at the ground frame-domain),
 *  named for testability. */
export type FrameShape = "rectangle" | "diamond" | "square" | "quatrefoil";

/**
 * The 2525 base frame shape an affiliation wears: friend=rectangle, hostile=DIAMOND,
 * neutral=square, unknown/pending=quatrefoil (garbage normalises to unknown⇒quatrefoil).
 * Single source of truth so the ALERT glyph's frame follows affiliation EXACTLY like the
 * unit markers — a hostile alert is a red diamond, not a neutral square. PURE + TOTAL. */
export function affiliationFrameShape(affiliation: string): FrameShape {
  switch (normAffil(affiliation)) {
    case "friend":
      return "rectangle";
    case "hostile":
      return "diamond";
    case "neutral":
      return "square";
    case "unknown":
    case "pending":
    default:
      return "quatrefoil";
  }
}

/**
 * A cached alert glyph sprite. Affiliation is the COLOR channel (a neutral NWS/
 * USGS alert stays green-tinted, a hostile one red); the TRIANGLE is the shape
 * channel saying "this is an event, not a unit". Canvas-rasterised with the same
 * DPR discipline as the unit frames; SSR/headless falls back to the transparent
 * pixel (never a throw, never a broken icon descriptor).
 */
export function alertIcon(
  style: AlertGlyphStyle,
  affiliation: string,
  colors: SymbolColors,
): SymbolIcon {
  const aff = normAffil(affiliation);
  const key = `alert|${style}|${aff}|${colors[aff]}`;
  const hit = cache.get(key);
  if (hit) return hit;

  if (typeof document === "undefined") return TRANSPARENT_PX;
  const dpr =
    typeof window !== "undefined" && window.devicePixelRatio
      ? Math.max(1, Math.min(window.devicePixelRatio, 3))
      : 1;
  const px = Math.round(SPRITE_CSS * dpr);
  const canvas = document.createElement("canvas");
  canvas.width = px;
  canvas.height = px;
  const ctx = canvas.getContext("2d");
  if (!ctx) return TRANSPARENT_PX;
  ctx.scale(dpr, dpr);

  const rgb = hexToRgb(colors[aff]);
  const cx = SPRITE_CSS / 2;
  const cy = SPRITE_CSS / 2;
  const r = SPRITE_CSS / 2 - (STROKE_CSS + 1);

  if (style === "minimal") drawAlertMinimal(ctx, cx, cy, r, rgb);
  else drawAlertFramed(ctx, cx, cy, r, rgb, aff);

  const icon: SymbolIcon = {
    url: canvas.toDataURL("image/png"),
    width: px,
    height: px,
    anchorX: px / 2,
    anchorY: px / 2,
  };
  cache.set(key, icon);
  return icon;
}

/** An "!" cut OUT of whatever is already drawn (destination-out ⇒ the basemap shows
 *  through) — the low-ink minimal treatment's exclamation. Bar + dot, rounded. */
function cutExclamation(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  r: number,
): void {
  ctx.save();
  ctx.globalCompositeOperation = "destination-out";
  ctx.fillStyle = "rgba(0,0,0,1)";
  const w = Math.max(2, r * 0.16);
  // bar
  ctx.beginPath();
  ctx.roundRect(cx - w / 2, cy - r * 0.42, w, r * 0.62, w / 2);
  ctx.fill();
  // dot
  ctx.beginPath();
  ctx.arc(cx, cy + r * 0.44, w * 0.62, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
}

/** MINIMAL alert glyph — a filled warning triangle (affiliation hue) with an "!"
 *  cutout. Low-ink, reads at density; the triangle alone says "event". */
function drawAlertMinimal(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  r: number,
  rgb: [number, number, number],
): void {
  ctx.save();
  ctx.lineJoin = "round";
  ctx.lineCap = "round";
  ctx.lineWidth = STROKE_CSS;
  ctx.strokeStyle = rgba(rgb, 1);
  ctx.fillStyle = rgba(rgb, 0.92);
  ctx.beginPath();
  ctx.moveTo(cx, cy - r);
  ctx.lineTo(cx + r * 0.95, cy + r * 0.72);
  ctx.lineTo(cx - r * 0.95, cy + r * 0.72);
  ctx.closePath();
  ctx.fill();
  ctx.stroke();
  ctx.restore();
  // "!" cutout — centred in the triangle's visual mass (a touch below centre).
  cutExclamation(ctx, cx, cy + r * 0.08, r * 0.82);
}

/** Trace the AFFILIATION frame sub-path for an alert glyph — the SAME base (ground)
 *  frame geometry the unit markers use (framePath_friend/hostile/neutral/unknown),
 *  dispatched by {@link affiliationFrameShape}. So an alert wears its affiliation's
 *  frame exactly like a unit of that affiliation (hostile=diamond, friend=rectangle,
 *  neutral=square, unknown/pending=quatrefoil). Path only — caller fills/strokes. */
function alertFramePath(
  ctx: CanvasRenderingContext2D,
  aff: Affil,
  cx: number,
  cy: number,
  r: number,
): void {
  switch (affiliationFrameShape(aff)) {
    case "rectangle":
      framePath_friend(ctx, cx, cy, r, "ground");
      break;
    case "diamond":
      framePath_hostile(ctx, cx, cy, r, "ground");
      break;
    case "square":
      framePath_neutral(ctx, cx, cy, r, "ground");
      break;
    case "quatrefoil":
      framePath_unknown(ctx, cx, cy, r, "ground");
      break;
  }
}

/** FRAMED alert glyph — the AFFILIATION frame (hostile=diamond, friend=rectangle,
 *  neutral=square, unknown/pending=quatrefoil — the SAME shape the unit markers wear,
 *  so a hostile alert reads as a red DIAMOND, not a neutral square) around a stroked
 *  warning triangle with an "!" that marks it as an EVENT, not a unit. The standard-
 *  symbology treatment for milsym + coalition modes.
 *  (Coalition-mode METOC-specific glyphs are a later refinement —
 *  TRACKED-TASK:TODO.md#coalition-metoc-glyphs.) */
function drawAlertFramed(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  r: number,
  rgb: [number, number, number],
  aff: Affil,
): void {
  ctx.save();
  ctx.lineJoin = "round";
  ctx.lineCap = "round";
  ctx.lineWidth = STROKE_CSS;
  ctx.strokeStyle = rgba(rgb, 1);
  // AFFILIATION frame, drawn like the unit frames: faint affiliation fill + crisp stroke
  // (pending gets the dashed "suspect" stroke). The frame IS the affiliation carrier.
  ctx.save();
  if (aff === "pending") ctx.setLineDash([4, 3]);
  ctx.fillStyle = rgba(rgb, FILL_ALPHA * 0.75); // faint fill so the interior "!" stays legible
  alertFramePath(ctx, aff, cx, cy, r);
  ctx.fill();
  ctx.stroke();
  ctx.restore();
  // Inner warning triangle (stroked — the frame carries the fill).
  const t = r * 0.52;
  ctx.beginPath();
  ctx.moveTo(cx, cy - t);
  ctx.lineTo(cx + t * 0.95, cy + t * 0.72);
  ctx.lineTo(cx - t * 0.95, cy + t * 0.72);
  ctx.closePath();
  ctx.stroke();
  // "!" inside the triangle, drawn in the hue (not a cutout — the frame fill is faint).
  const w = Math.max(1.5, t * 0.2);
  ctx.fillStyle = rgba(rgb, 1);
  ctx.beginPath();
  ctx.roundRect(cx - w / 2, cy - t * 0.32, w, t * 0.56, w / 2);
  ctx.fill();
  ctx.beginPath();
  ctx.arc(cx, cy + t * 0.5, w * 0.6, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
}

// ── DOT-MODE HOSTILE DIAMOND — the Minimal carrier's hostility shape cue ─────

/**
 * A cached MASK sprite of a filled diamond (square rotated 45°) — the MIL-STD-2525
 * hostile frame shape carried into the MINIMAL ("dot") marker mode, where every
 * marker used to be a circle and hostility read ONLY through hue (strip the hue —
 * the MONO master test — and a hostile was indistinguishable from a friend).
 *
 * Drawn OPAQUE WHITE on transparency because the consumer sets `mask: true` on the
 * deck.gl icon descriptor: deck then reads ONLY the sprite's alpha channel and tints
 * the shape with the per-object `getColor` — so the dot layer's full per-entity fill
 * pipeline (affiliation hue + altitude tint + encrypted-payload alpha + fade) is
 * preserved exactly; only the SHAPE changes. One palette-independent sprite serves
 * every theme (cached once; DPR-sharp like the frame sprites). SSR/headless falls
 * back to the transparent pixel — never a throw, never a broken descriptor.
 */
export function dotDiamondIcon(): SymbolIcon {
  const key = "dotshape|diamond";
  const hit = cache.get(key);
  if (hit) return hit;

  if (typeof document === "undefined") return TRANSPARENT_PX;
  const dpr =
    typeof window !== "undefined" && window.devicePixelRatio
      ? Math.max(1, Math.min(window.devicePixelRatio, 3))
      : 1;
  const px = Math.round(SPRITE_CSS * dpr);
  const canvas = document.createElement("canvas");
  canvas.width = px;
  canvas.height = px;
  const ctx = canvas.getContext("2d");
  if (!ctx) return TRANSPARENT_PX;
  ctx.scale(dpr, dpr);

  const cx = SPRITE_CSS / 2;
  const cy = SPRITE_CSS / 2;
  const d = SPRITE_CSS / 2 - 1; // tip half-diagonal; 1px pad keeps the AA edge in-bounds
  ctx.fillStyle = "rgba(255,255,255,1)"; // mask sprite: only the ALPHA channel matters
  ctx.beginPath();
  ctx.moveTo(cx, cy - d);
  ctx.lineTo(cx + d, cy);
  ctx.lineTo(cx, cy + d);
  ctx.lineTo(cx - d, cy);
  ctx.closePath();
  ctx.fill();

  const icon: SymbolIcon = {
    url: canvas.toDataURL("image/png"),
    width: px,
    height: px,
    anchorX: px / 2,
    anchorY: px / 2,
  };
  cache.set(key, icon);
  return icon;
}

// ── Rasterisation ────────────────────────────────────────────────────────────

function draw(aff: Affil, dom: Domain, colors: SymbolColors): SymbolIcon {
  if (typeof document === "undefined") return TRANSPARENT_PX;

  const dpr =
    typeof window !== "undefined" && window.devicePixelRatio
      ? Math.max(1, Math.min(window.devicePixelRatio, 3))
      : 1;

  const px = Math.round(SPRITE_CSS * dpr);
  const canvas = document.createElement("canvas");
  canvas.width = px;
  canvas.height = px;
  const ctx = canvas.getContext("2d");
  if (!ctx) return TRANSPARENT_PX;

  // Work in CSS-pixel coordinates; the DPR scale handles retina crispness.
  ctx.scale(dpr, dpr);

  const rgb = hexToRgb(colors[aff]);
  const stroke = rgba(rgb, 1);
  const fill = rgba(rgb, FILL_ALPHA);

  ctx.lineWidth = STROKE_CSS;
  ctx.lineJoin = "round";
  ctx.lineCap = "round";
  ctx.strokeStyle = stroke;
  ctx.fillStyle = fill;

  // Half-stroke inset (STROKE_CSS + 1) keeps the stroke fully inside the sprite bounds.
  const pad = STROKE_CSS + 1;
  const cx = SPRITE_CSS / 2;
  const cy = SPRITE_CSS / 2;
  const r = SPRITE_CSS / 2 - pad; // frame "radius" (half-extent)

  drawFrame(ctx, aff, dom, cx, cy, r);
  drawDimension(ctx, dom, cx, cy, r, stroke);
  drawGlyph(ctx, dom, cx, cy, r, stroke);

  return {
    url: canvas.toDataURL("image/png"),
    width: px,
    height: px,
    anchorX: px / 2,
    anchorY: px / 2,
  };
}

/** Canonical 2525 frame dimension the affiliation shape is restyled into. */
type FrameDomain = "ground" | "sea" | "air" | "subsurface";

/** Map the COP's domain enum onto a canonical 2525 frame dimension. No current
 *  feed emits "subsurface"; air/uas/space all read as the open-base air frame
 *  (space additionally gets the above-frame cap in drawDimension). */
function frameDomainOf(dom: Domain): FrameDomain {
  switch (dom) {
    case "maritime":
      return "sea";
    case "air":
    case "uas":
    case "space":
      return "air";
    case "subsurface":
      return "subsurface";
    case "ground":
    default:
      return "ground";
  }
}

/** Draw the affiliation FRAME (filled + stroked), restyled by frame-domain.
 *  `pending` uses a dashed line; air/subsurface frames are intentionally left
 *  open on the base/top edge (fill() auto-closes them flat while stroke() shows
 *  the open edge). */
function drawFrame(
  ctx: CanvasRenderingContext2D,
  aff: Affil,
  dom: Domain,
  cx: number,
  cy: number,
  r: number,
): void {
  const fd = frameDomainOf(dom);

  ctx.save();
  if (aff === "pending") ctx.setLineDash([4, 3]);

  switch (aff) {
    case "friend":
      framePath_friend(ctx, cx, cy, r, fd);
      break;
    case "hostile":
      framePath_hostile(ctx, cx, cy, r, fd);
      break;
    case "neutral":
      framePath_neutral(ctx, cx, cy, r, fd);
      break;
    case "unknown":
    case "pending":
      framePath_unknown(ctx, cx, cy, r, fd);
      break;
  }

  // Fill first (open air/subsurface paths fill as if closed), then stroke.
  ctx.fill();
  ctx.stroke();
  ctx.restore();
}

/** Centred circle sub-path — the sea-surface frame for friend/hostile. */
function framePath_circle(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  rad: number,
): void {
  ctx.beginPath();
  ctx.arc(cx, cy, rad, 0, Math.PI * 2);
}

/** Arch sub-path — a semicircular feature end, vertical sides, open flat end.
 *  dir = +1 → rounded TOP / open bottom (air); dir = -1 → rounded BOTTOM /
 *  open top (subsurface). The open edge is left untraced so stroke() shows it
 *  open while fill() closes it flat. */
function framePath_arch(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  r: number,
  dir: 1 | -1,
): void {
  const w = r * 0.8; // half-width == semicircle radius
  const featureY = cy - dir * r; // arch tip (top when dir = +1)
  const springY = featureY + dir * w; // straight-side ↔ semicircle junction
  const openY = cy + dir * (r * 0.74); // open base edge
  ctx.beginPath();
  ctx.moveTo(cx - w, openY);
  ctx.lineTo(cx - w, springY);
  // Semicircle over the feature end: anticlockwise flag flips with dir so the
  // bulge stays on the feature side (top for air, bottom for subsurface).
  ctx.arc(cx, springY, w, Math.PI, 0, dir === -1);
  ctx.lineTo(cx + w, openY);
  // Open base: no closePath.
}

/** Peak/pentagon sub-path — a pointed feature end, shoulders, vertical sides,
 *  open flat end. dir = +1 → peak up / open bottom (hostile air);
 *  dir = -1 → peak down / open top (hostile subsurface). */
function framePath_peak(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  r: number,
  dir: 1 | -1,
): void {
  const w = r * 0.74;
  const peakY = cy - dir * r;
  const shoulderY = cy - dir * (r * 0.42);
  const openY = cy + dir * (r * 0.74);
  ctx.beginPath();
  ctx.moveTo(cx - w, openY);
  ctx.lineTo(cx - w, shoulderY);
  ctx.lineTo(cx, peakY);
  ctx.lineTo(cx + w, shoulderY);
  ctx.lineTo(cx + w, openY);
  // Open base: no closePath.
}

/** Friendly frame — ground=rectangle, sea=circle, air=arch, subsurface=inverted arch. */
function framePath_friend(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  r: number,
  fd: FrameDomain,
): void {
  if (fd === "sea") {
    framePath_circle(ctx, cx, cy, r * 0.92);
    return;
  }
  if (fd === "air" || fd === "subsurface") {
    framePath_arch(ctx, cx, cy, r, fd === "air" ? 1 : -1);
    return;
  }
  // ground: rectangle (the friendly frame reads wider than tall).
  const w = r * 0.98;
  const h = r * 0.74;
  ctx.beginPath();
  ctx.rect(cx - w, cy - h, 2 * w, 2 * h);
}

/** Hostile frame — ground=diamond, sea=DIAMOND, air=peak, subsurface=inverted peak.
 *  MIL-STD-2525: the hostile SEA-SURFACE frame is the SAME diamond as ground — only the
 *  FRIEND sea-surface frame is a circle. This used to draw the hostile sea frame as a
 *  circle, so in a maritime theater (Hormuz) every hostile surface contact wore a
 *  friend-shaped frame; the frame IS the affiliation carrier, so it must be the diamond. */
function framePath_hostile(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  r: number,
  fd: FrameDomain,
): void {
  if (fd === "air" || fd === "subsurface") {
    framePath_peak(ctx, cx, cy, r, fd === "air" ? 1 : -1);
    return;
  }
  // ground + sea-surface: diamond (square rotated 45°).
  const d = r * 1.12;
  ctx.beginPath();
  ctx.moveTo(cx, cy - d);
  ctx.lineTo(cx + d, cy);
  ctx.lineTo(cx, cy + d);
  ctx.lineTo(cx - d, cy);
  ctx.closePath();
}

/** Neutral frame — square in every dimension; air opens the base, subsurface
 *  opens the top (the std keeps neutral square/rectangular throughout). */
function framePath_neutral(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  r: number,
  fd: FrameDomain,
): void {
  const s = r * 0.86;
  const l = cx - s;
  const rt = cx + s;
  const t = cy - s;
  const b = cy + s;
  ctx.beginPath();
  if (fd === "air") {
    // Open bottom edge.
    ctx.moveTo(l, b);
    ctx.lineTo(l, t);
    ctx.lineTo(rt, t);
    ctx.lineTo(rt, b);
  } else if (fd === "subsurface") {
    // Open top edge.
    ctx.moveTo(l, t);
    ctx.lineTo(l, b);
    ctx.lineTo(rt, b);
    ctx.lineTo(rt, t);
  } else {
    ctx.rect(l, t, rt - l, b - t);
  }
}

/** Unknown / pending frame — a true rounded CLOVERLEAF (quatrefoil).
 *
 *  Construction (milsymbol-style): four lobe circles of radius `Rp` sit on the
 *  cardinal axes; the outline is each lobe's outward arc between two diagonal
 *  "corner" cusps at (cx±q, cy±q). Consecutive arcs share a corner exactly, so
 *  the path is continuous and adjacent lobes meet at clean re-entrant notches —
 *  no spikes. `Rp` only slightly exceeds `q`, so each lobe is a fat ~140° arc
 *  reaching the frame edge while the diagonal cusps tuck inward.
 *
 *  air        → drop the bottom lobe, leave the base open (3-lobe "cloud").
 *  subsurface → drop the top lobe, leave the top open (inverted 3-lobe).
 *  ground/sea → full 4-lobe quatrefoil. */
function framePath_unknown(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  r: number,
  fd: FrameDomain,
): void {
  const q = r * 0.58; // diagonal cusp half-offset
  const Rp = r * 0.62; // lobe-circle radius (only just > q → fat round lobes)
  const m = Math.sqrt(Math.max(0, Rp * Rp - q * q));

  // Diagonal corner cusps.
  const TL: [number, number] = [cx - q, cy - q];
  const TR: [number, number] = [cx + q, cy - q];
  const BR: [number, number] = [cx + q, cy + q];
  const BL: [number, number] = [cx - q, cy + q];

  // Each lobe: circle centre on a cardinal axis + the two corners it spans.
  type Lobe = { c: [number, number]; a: [number, number]; b: [number, number] };
  const top: Lobe = { c: [cx, cy - q + m], a: TL, b: TR };
  const right: Lobe = { c: [cx + q - m, cy], a: TR, b: BR };
  const bottom: Lobe = { c: [cx, cy + q - m], a: BR, b: BL };
  const left: Lobe = { c: [cx - q + m, cy], a: BL, b: TL };

  // Draw a lobe's OUTWARD arc. Increasing angle (anticlockwise=false) sweeps the
  // cardinal bulge for every lobe, by construction of the centres/corners above.
  const lobeArc = (p: Lobe): void => {
    const [lx, ly] = p.c;
    const a1 = Math.atan2(p.a[1] - ly, p.a[0] - lx);
    const a2 = Math.atan2(p.b[1] - ly, p.b[0] - lx);
    ctx.arc(lx, ly, Rp, a1, a2, false);
  };

  ctx.beginPath();
  if (fd === "air") {
    // Left → top → right lobes; base left open.
    lobeArc(left);
    lobeArc(top);
    lobeArc(right);
  } else if (fd === "subsurface") {
    // Right → bottom → left lobes; top left open.
    lobeArc(right);
    lobeArc(bottom);
    lobeArc(left);
  } else {
    lobeArc(top);
    lobeArc(right);
    lobeArc(bottom);
    lobeArc(left);
    ctx.closePath();
  }
}

/** Dimension modifier — an above-frame treatment that hints the domain.
 *  air/uas: small up-chevron over the (open) frame; space: top arc cap. */
function drawDimension(
  ctx: CanvasRenderingContext2D,
  dom: Domain,
  cx: number,
  cy: number,
  r: number,
  stroke: string,
): void {
  ctx.save();
  ctx.strokeStyle = stroke;
  ctx.lineWidth = STROKE_CSS;
  ctx.lineCap = "round";
  ctx.lineJoin = "round";

  if (dom === "air" || dom === "uas") {
    // Up-chevron seated just above the frame top → "air" dimension.
    const top = cy - r - 1;
    const cw = r * 0.42;
    ctx.beginPath();
    ctx.moveTo(cx - cw, top);
    ctx.lineTo(cx, top - cw * 0.85);
    ctx.lineTo(cx + cw, top);
    ctx.stroke();
  } else if (dom === "space") {
    // Top arc cap → "space" (above-air) dimension.
    const top = cy - r - 1;
    const aw = r * 0.6;
    ctx.beginPath();
    ctx.arc(cx, top + aw * 0.4, aw, Math.PI * 1.15, Math.PI * 1.85);
    ctx.stroke();
  }
  // maritime / ground: standard closed frame, no above-frame modifier.
  ctx.restore();
}

/** Central domain GLYPH. Drawn in the affiliation hue at full strength. */
function drawGlyph(
  ctx: CanvasRenderingContext2D,
  dom: Domain,
  cx: number,
  cy: number,
  r: number,
  stroke: string,
): void {
  ctx.save();
  ctx.strokeStyle = stroke;
  ctx.fillStyle = stroke;
  ctx.lineWidth = Math.max(1.5, STROKE_CSS - 0.25);
  ctx.lineCap = "round";
  ctx.lineJoin = "round";

  const g = r * 0.5; // glyph half-extent

  switch (dom) {
    case "air": {
      // Up-wedge / arrowhead.
      ctx.beginPath();
      ctx.moveTo(cx, cy - g);
      ctx.lineTo(cx + g * 0.8, cy + g * 0.6);
      ctx.lineTo(cx, cy + g * 0.2);
      ctx.lineTo(cx - g * 0.8, cy + g * 0.6);
      ctx.closePath();
      ctx.stroke();
      break;
    }
    case "uas": {
      // Small fixed-wing planform: fuselage + swept wings + tail.
      ctx.beginPath();
      ctx.moveTo(cx, cy - g); // nose
      ctx.lineTo(cx, cy + g * 0.85); // tail
      ctx.moveTo(cx - g * 0.95, cy + g * 0.1); // left wing tip
      ctx.lineTo(cx + g * 0.95, cy + g * 0.1); // right wing tip
      ctx.moveTo(cx - g * 0.4, cy + g * 0.7); // tailplane
      ctx.lineTo(cx + g * 0.4, cy + g * 0.7);
      ctx.stroke();
      break;
    }
    case "subsurface":
    case "maritime": {
      // Ship-hull stroke (a shallow U) with a short mast. Subsurface shares it;
      // the open-top frame already distinguishes sub-surface from sea-surface.
      ctx.beginPath();
      ctx.moveTo(cx - g, cy);
      ctx.lineTo(cx - g * 0.7, cy + g * 0.7);
      ctx.lineTo(cx + g * 0.7, cy + g * 0.7);
      ctx.lineTo(cx + g, cy);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(cx, cy);
      ctx.lineTo(cx, cy - g * 0.8);
      ctx.stroke();
      break;
    }
    case "ground": {
      // Cross + centre dot.
      ctx.beginPath();
      ctx.moveTo(cx - g * 0.7, cy);
      ctx.lineTo(cx + g * 0.7, cy);
      ctx.moveTo(cx, cy - g * 0.7);
      ctx.lineTo(cx, cy + g * 0.7);
      ctx.stroke();
      ctx.beginPath();
      ctx.arc(cx, cy, Math.max(1.4, g * 0.18), 0, Math.PI * 2);
      ctx.fill();
      break;
    }
    case "space": {
      // 4-point star.
      ctx.beginPath();
      ctx.moveTo(cx, cy - g);
      ctx.lineTo(cx + g * 0.28, cy - g * 0.28);
      ctx.lineTo(cx + g, cy);
      ctx.lineTo(cx + g * 0.28, cy + g * 0.28);
      ctx.lineTo(cx, cy + g);
      ctx.lineTo(cx - g * 0.28, cy + g * 0.28);
      ctx.lineTo(cx - g, cy);
      ctx.lineTo(cx - g * 0.28, cy - g * 0.28);
      ctx.closePath();
      ctx.stroke();
      break;
    }
  }
  ctx.restore();
}
